import React, { useEffect, useRef, useState } from 'react';

import { Modal } from '@wds/modal';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Typography } from '@wds/typography';

import { getCookie } from 'sma.common/utils';

import PropTypes from 'prop-types';
import STRINGS from '../../constants';
import AUTH_STRINGS from '../Authentication/constants';
import MFAErrorMessage from '../Authentication/components/MFAErrorMessage';
import RecoveryEmailChallengeModal from './RecoveryEmailChallengeModal';
import { clearCooldownTimer, startCooldownTimer } from '../Authentication/utils/cooldown';

const DeactivateMFAModal = (props) => {
	const {
		open,
		setOpen,
		handleOnSuccess,
		method,
	} = props;
	const [challengeCode, setChallengeCode] = useState('');
	const [error, setError] = useState('');
	const [isValid, setIsValid] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [message, setMessage] = useState('');
	const [showRecoveryModal, setShowRecoveryModal] = useState(false);
	const sentOnceRef = React.useRef(false);
	const timerRef = useRef(null);
	const cooldownEndAtRef = useRef(0);
	const previousCooldownRef = useRef(0);

	const RESEND_COOLDOWN_SEC = 120;

	useEffect(() => {
		if (challengeCode.match(/^[0-9]+$/) && challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
	}, [challengeCode]);

	useEffect(() => {
		return () => {
			clearCooldownTimer(timerRef);
		};
	}, []);

	useEffect(() => {
		const previousCooldown = previousCooldownRef.current;
		if (
			previousCooldown > 0
			&& cooldown === 0
			&& message === STRINGS.code_already_pending
		) {
			setMessage('');
		}

		previousCooldownRef.current = cooldown;
	}, [cooldown, message]);

	// Auto-send email code when modal opens for email method (matches other modals)
	useEffect(() => {
		if (open && method === 'email' && !sentOnceRef.current) {
			sentOnceRef.current = true;
			sendEmailCode();
		}
		
		// Track MFA removal started when modal opens
		if (open && window.track) {
			window.track('mfaRemovalStarted', {
				method: method,
			});
		}
	}, [open, method]);

	const removeMFA = async () => {
		try {
			setIsLoading(true);
			const response = await fetch(reverse('api-internal-mfa-remove'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ code: challengeCode, action: 'remove_mfa', method }),
			});
			return await response.json();
		} catch (err) {
			console.log(err);
			setError(STRINGS.unknownError);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOnClose = () => {
		setOpen(false);
	};

	const handleErrorResponse = async (response) => {
		const errorData = await response.json();
		if (response.status === 401 || response.status === 400 || response.status === 403) {
			setMessage('');
			setError(errorData.error);
		} else {
			setMessage('');
			setError('unknownError');
		}
	};

	const handleError = (msg) => {
		setMessage('');
		setError(msg);
	};

	const handleDeactivateMFA = () => {
		removeMFA()
			.then((data) => {
				if (data && data.error) {
					if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
						window.location.href = reverse('mfa-lockout');
						return;
					}
					setMessage('');
					setError(data.error);
				} else {
					setError('');
					setChallengeCode('');
					handleOnSuccess();
				}
			})
			.catch((err) => {
				setMessage('');
				setError(err.message || STRINGS.unknownError);
			});
	};

	const handleOnCancel = () => {
		setChallengeCode('');
		handleOnClose();
	};

	const handleRecoveryVerified = async () => {
		setShowRecoveryModal(false);
		setIsLoading(true);
		try {
			const response = await fetch(reverse('api-internal-mfa-remove'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action: 'remove_mfa', use_recovery_email: true }),
			});
			const data = await response.json();
			if (data && data.error) {
				setError(data.error);
			} else {
				setError('');
				setChallengeCode('');
				handleOnSuccess();
			}
		} catch (err) {
			setError(STRINGS.unknownError);
		} finally {
			setIsLoading(false);
		}
	};

	const startCooldown = (remaining) => {
		startCooldownTimer({
			remaining,
			defaultSeconds: RESEND_COOLDOWN_SEC,
			setCooldown,
			timerRef,
			endAtRef: cooldownEndAtRef,
		});
	};

	const sendEmailCode = async () => {
		if (sending || cooldown > 0) return;
		setSending(true);
		setError('');
		setMessage('');
		try {
			const resp = await fetch(reverse('api-internal-mfa-challenge'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action: 'send_code', method: 'email', context: 'mfa_settings' }),
				credentials: 'same-origin',
			});
			const data = await resp.json();
			if (data.success) {
					setMessage(STRINGS.code_sent);
					// Clear any previous verification errors when a new code is sent
					setError('');
				sentOnceRef.current = true;
				startCooldown();
			} else if (data.message) {
					setMessage(STRINGS?.[data.message]);
					setError('');
			} else if (data.error) {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
						// Show informational pending message instead of an error
						setMessage(STRINGS.code_already_pending);
						setError('');
					startCooldown(data.time_remaining);
				}
			} else {
				setMessage(STRINGS.unknownError);
			}
		} catch (err) {
			handleError(`${STRINGS.errorHasOccurred}: ${err}`);
		} finally {
			setSending(false);
		}
	};

	const inputColor = error ? 'error' : '';

	return (
		<Box id='sma-deactivate-mfa-modal'>
			<Modal
				show={open}
				data-testid='mfa-deactivate-mfa-modal'
				onCancel={handleOnCancel}
				onClose={handleOnClose}
			>
				<Modal.Header header={STRINGS.deactivateMFATitle} />
				<Modal.Body>
					<Box display='flex' gap={2} mb={2} flexDirection='column'>
				<Typography component='p' variant='bodySm'>
					{STRINGS.deactivateMFAWarning}
				</Typography>
				<Typography component='p' variant='bodySm'>
					{method === 'totp' ? STRINGS.deactivateMFAContinueTOTP : STRINGS.deactivateMFAContinueEmail}
				</Typography>
						{message && (
							<Box
								p={4}
								style={{
									backgroundColor: '#42ac14',
									borderRadius: '5px',
									color: '#FFFFFF',
								}}
								role='alert'
								aria-live='assertive'
							>
								{STRINGS?.[message] || message}
							</Box>
						)}
						<MFAErrorMessage error={error} />
					<Input
						value={challengeCode}
						placeholder={STRINGS.enterCodeProvided}
						onChange={(e) => setChallengeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
						id='mfa-challenge-code'
						type='text'
						inputMode='numeric'
						maxLength={6}
						color={inputColor}
						stretched
						aria-required='true'
						//errorMessage={error ? (STRINGS?.[error] || error || STRINGS.unknownError) : undefined}
						autoFocus
					/>
					{method === 'email' && (
						<Box style={{ textAlign: 'right' }}>
							<Typography
								component='span'
								onClick={(cooldown > 0 || sending || isLoading) ? undefined : sendEmailCode}
								style={{
									color: (cooldown > 0 || sending || isLoading) ? '#768086' : '#007FAA',
									cursor: (cooldown > 0 || sending || isLoading) ? 'not-allowed' : 'pointer',
									fontSize: '15px',
									fontWeight: '500'
								}}
							>
							{sending
								? AUTH_STRINGS.sending
								: cooldown > 0
								? `${AUTH_STRINGS.resendCode} (${cooldown}s)`
								: AUTH_STRINGS.resendCode}
							</Typography>
						</Box>
					)}
					<Box mt={2}>
						<Typography component='p' variant='bodySm' style={{ color: '#6B7280', marginBottom: '8px' }}>
							{AUTH_STRINGS.doNotHaveAccessToDevice}
						</Typography>
						<a
							href='#'
							onClick={(e) => {
								e.preventDefault();
								setShowRecoveryModal(true);
							}}
							style={{ color: '#0EA5E9', textDecoration: 'none', fontSize: '14px' }}
						>
							{AUTH_STRINGS.recoverAccount}
						</a>
					</Box>
				</Box>
				</Modal.Body>
				<Modal.Footer align='right'>
					<Button
						onClick={handleOnClose}
						variant='text'
						color='secondary'
						id='sma-deactviate-mfa-modal-cancel-button'
						aria-label={STRINGS.cancelBtn}
					>
						{STRINGS.cancelBtn}
					</Button>
					<Button
						onClick={() => handleDeactivateMFA()}
						id='sma-deactviate-mfa-button'
						color='warning'
						aria-label={STRINGS.deactivateMFA}
						disabled={!isValid || isLoading}
						aria-disabled={!isValid}
					>
						{STRINGS.deactivateMFA}
					</Button>
				</Modal.Footer>
			</Modal>

			<RecoveryEmailChallengeModal
				open={showRecoveryModal}
				handleClose={() => setShowRecoveryModal(false)}
				onVerified={handleRecoveryVerified}
			/>
		</Box>
	);
};

DeactivateMFAModal.propTypes = {
	open: PropTypes.bool.isRequired,
	setOpen: PropTypes.func.isRequired,
	handleOnSuccess: PropTypes.func.isRequired,
	method: PropTypes.string.isRequired,
};

export default DeactivateMFAModal;
